import type {
  TradeCategory,
  ProviderTarget,
  FloorplanSnapshot,
} from '../types/poptavky'
import type { SelectedLocationData } from '../components/LocationPickerMap'

export const LS_SAVED_INQUIRY_DRAFT_KEY = 'nacenito_saved_inquiry_draft_v1'

export interface SavedInquiryDraftState {
  step: 1 | 2 | 3
  selectedCategories: TradeCategory[]
  selectedSubTasks: string[]
  multiCategoryPublishMode: 'combined' | 'split'
  providerTargetChoice: ProviderTarget
  scaleSummary: string
  title: string
  isTitleAutoGenerated: boolean
  description: string
  aiPromptInput: string
  aiFilledSuccess: boolean
  photos: string[]
  attachedFloorplan?: FloorplanSnapshot
  locationData: SelectedLocationData
  urgency: 'flexible' | 'within_month' | 'urgent'
  expectedDate: string
  estimatedBudget: string
  clientName: string
  clientPhone: string
  clientEmail: string
  updatedAt: number
}

/**
 * Zkontroluje, zda rozpracovaný návrh obsahuje smysluplné úpravy od uživatele,
 * aby se neukládal prázdný výchozí stav.
 */
export function isMeaningfulDraft(draft: Partial<SavedInquiryDraftState> | null | undefined): boolean {
  if (!draft) return false
  if ((draft.step && draft.step > 1) || draft.aiFilledSuccess) return true
  if (draft.title && draft.title.trim().length > 0 && !draft.isTitleAutoGenerated) return true
  if (draft.description && draft.description.trim().length > 0) return true
  if (draft.aiPromptInput && draft.aiPromptInput.trim().length > 0) return true
  if (draft.selectedSubTasks && draft.selectedSubTasks.length > 0) return true
  if (draft.photos && draft.photos.length > 0) return true
  if (draft.attachedFloorplan) return true
  if (draft.selectedCategories && draft.selectedCategories.length > 1) return true
  if (draft.clientPhone && draft.clientPhone.trim().length > 0) return true
  return false
}

/**
 * Načte rozpracovanou poptávku z LocalStorage (funguje i pro nepřihlášené návštěvníky).
 */
export function loadSavedInquiryDraft(): SavedInquiryDraftState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(LS_SAVED_INQUIRY_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedInquiryDraftState
    if (!isMeaningfulDraft(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Automaticky uloží rozpracovanou poptávku do LocalStorage.
 */
export function saveInquiryDraftState(state: SavedInquiryDraftState): void {
  if (typeof window === 'undefined') return
  try {
    if (!isMeaningfulDraft(state)) {
      return
    }
    localStorage.setItem(LS_SAVED_INQUIRY_DRAFT_KEY, JSON.stringify(state))
  } catch {
    // Pokud by fotky v Base64 překročily kvótu LocalStorage, uložíme návrh bez fotek
    try {
      const compactState: SavedInquiryDraftState = {
        ...state,
        photos: (state.photos || []).filter((p) => !p.startsWith('data:')),
      }
      localStorage.setItem(LS_SAVED_INQUIRY_DRAFT_KEY, JSON.stringify(compactState))
    } catch {
      // Ignorovat chybu kvóty
    }
  }
}

/**
 * Vymaže rozpracovanou poptávku (např. po úspěšném odeslání nebo na žádost uživatele).
 */
export function clearSavedInquiryDraft(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(LS_SAVED_INQUIRY_DRAFT_KEY)
  } catch {
    // Ignorovat
  }
}
